import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  Client,
  Medication,
  NoteType,
  Provider,
  SessionNote,
  SessionState,
} from '@/types/session';
import type { BenefitFilterState, BenefitKey } from '@/types/plans';

function uid(prefix = ''): string {
  const r = Math.random().toString(36).slice(2, 10);
  return prefix ? `${prefix}_${Date.now().toString(36)}${r}` : `${Date.now().toString(36)}${r}`;
}

function emptyClient(): Client {
  return {
    name: '',
    phone: '',
    dob: '',
    zip: '',
    county: '',
    state: null,
    planType: null,
    medicaidConfirmed: false,
  };
}

function emptyBenefitFilters(): BenefitFilterState {
  const off = {
    enabled: false,
    subToggles: {},
    tier: 'any' as const,
  };
  return {
    dental: { ...off },
    vision: { ...off },
    hearing: { ...off },
    transportation: { ...off },
    otc: { ...off },
    food_card: { ...off },
    diabetic: { ...off },
    fitness: { ...off },
  };
}

/** A dental plan the broker added to the client quote from the Dental screen.
 *  The premium is what src/lib/dentalPricing.ts priced for the inputs on that
 *  screen; the consumer API re-reads every plan fact from pm_dental_plan. */
export interface QuoteDentalPick {
  planId: string;
  carrier: string;
  planName: string;
  monthlyPremium: number;
  startingAt: boolean;
  /** Who the premium covers, e.g. "You and your spouse". */
  coveredLabel: string;
}

function initialState(): SessionState & { benefitFilters: BenefitFilterState; quoteDental: QuoteDentalPick[] } {
  return {
    sessionId: uid('ses'),
    startedAt: Date.now(),
    isAnnualReview: false,
    client: emptyClient(),
    medications: [],
    providers: [],
    notes: [],
    plansCompared: [],
    recommendation: null,
    complianceChecked: [],
    disclaimersConfirmed: [],
    complianceTimestamps: {},
    disclaimerTimestamps: {},
    currentPlanId: null,
    currentPlanName: null,
    noCurrentPlan: false,
    selectedFinalists: [],
    givebackPlanEnrolled: false,
    benefitFilters: emptyBenefitFilters(),
    quoteDental: [],
  };
}

/** Most dental plans one quote can carry (mirrors MAX_DENTAL_PLANS in the
 *  consumer api/_lib/quote-dental.ts). */
export const MAX_QUOTE_DENTAL = 3;

interface SessionStore extends SessionState {
  benefitFilters: BenefitFilterState;
  quoteDental: QuoteDentalPick[];
  /** Add (or refresh the price of) a dental plan on the quote. No-op past MAX_QUOTE_DENTAL. */
  upsertQuoteDental: (pick: QuoteDentalPick) => void;
  removeQuoteDental: (planId: string) => void;
  setIsAnnualReview: (flag: boolean) => void;
  updateClient: (patch: Partial<Client>) => void;
  addMedication: (med: Omit<Medication, 'id' | 'addedAt'>) => string;
  updateMedication: (id: string, patch: Partial<Medication>) => void;
  removeMedication: (id: string) => void;
  addProvider: (p: Omit<Provider, 'id' | 'addedAt'>) => string;
  updateProvider: (id: string, patch: Partial<Provider>) => void;
  removeProvider: (id: string) => void;
  addNote: (type: NoteType, body: string, opts?: { carrier?: string; scenario?: string }) => void;
  removeNote: (id: string) => void;
  setCurrentPlanId: (id: string | null) => void;
  setCurrentPlanName: (name: string | null) => void;
  setNoCurrentPlan: (flag: boolean) => void;
  setBenefitFilter: (key: BenefitKey, patch: Partial<import('@/types/plans').BenefitFilter>) => void;
  resetBenefitFilters: () => void;
  setSelectedFinalists: (ids: string[]) => void;
  setRecommendation: (id: string | null) => void;
  setGivebackPlanEnrolled: (enrolled: boolean) => void;
  toggleComplianceItem: (id: string) => void;
  confirmDisclaimer: (id: string) => void;
  resetSession: () => void;
}

export const useSession = create<SessionStore>()(
  persist(
    (set) => ({
      ...initialState(),

      setIsAnnualReview: (flag) => set({ isAnnualReview: flag }),

      upsertQuoteDental: (pick) =>
        set((state) => {
          const i = state.quoteDental.findIndex((d) => d.planId === pick.planId);
          if (i >= 0) {
            const next = state.quoteDental.slice();
            next[i] = pick;
            return { quoteDental: next };
          }
          if (state.quoteDental.length >= MAX_QUOTE_DENTAL) return state;
          return { quoteDental: [...state.quoteDental, pick] };
        }),

      removeQuoteDental: (planId) =>
        set((state) => ({ quoteDental: state.quoteDental.filter((d) => d.planId !== planId) })),

      updateClient: (patch) =>
        set((state) => ({ client: { ...state.client, ...patch } })),

      addMedication: (med) => {
        const id = uid('med');
        set((state) => ({
          medications: [
            ...state.medications,
            { ...med, id, addedAt: Date.now() },
          ],
        }));
        return id;
      },

      updateMedication: (id, patch) =>
        set((state) => ({
          medications: state.medications.map((m) =>
            m.id === id ? { ...m, ...patch } : m,
          ),
        })),

      removeMedication: (id) =>
        set((state) => ({
          medications: state.medications.filter((m) => m.id !== id),
        })),

      addProvider: (p) => {
        const id = uid('prv');
        set((state) => ({
          providers: [
            ...state.providers,
            { ...p, id, addedAt: Date.now() },
          ],
        }));
        return id;
      },

      updateProvider: (id, patch) =>
        set((state) => ({
          providers: state.providers.map((p) => (p.id === id ? { ...p, ...patch } : p)),
        })),

      removeProvider: (id) =>
        set((state) => ({
          providers: state.providers.filter((p) => p.id !== id),
        })),

      addNote: (type, body, opts) => {
        const trimmed = body.trim();
        if (!trimmed) return;
        set((state) => ({
          notes: [
            {
              id: uid('note'),
              type,
              body: trimmed,
              createdAt: Date.now(),
              ...(opts?.carrier ? { carrier: opts.carrier } : {}),
              ...(opts?.scenario ? { scenario: opts.scenario } : {}),
            },
            ...state.notes,
          ],
        }));
      },

      removeNote: (id) =>
        set((state) => ({ notes: state.notes.filter((n) => n.id !== id) })),

      setCurrentPlanId: (id) =>
        // Selecting a real plan implicitly clears the "new to
        // Medicare" flag (the broker just told us what plan the
        // client is on). Setting to null leaves noCurrentPlan
        // alone — the broker may be clearing the picker on the
        // way to clicking "New to Medicare". Always resets the
        // display name — callers that want a name (AgentBase
        // hydration, CurrentPlanPicker) must follow with
        // setCurrentPlanName so a stale name from a prior client
        // can't stick to the new id.
        set((s) => ({
          currentPlanId: id,
          currentPlanName: null,
          noCurrentPlan: id ? false : s.noCurrentPlan,
        })),
      setCurrentPlanName: (name) => set({ currentPlanName: name }),
      setNoCurrentPlan: (flag) =>
        // Marking "new to Medicare" clears any previously-picked
        // plan so the two states stay mutually exclusive.
        set((s) => ({
          noCurrentPlan: flag,
          currentPlanId: flag ? null : s.currentPlanId,
          currentPlanName: flag ? null : s.currentPlanName,
        })),

      setBenefitFilter: (key, patch) =>
        set((state) => ({
          benefitFilters: {
            ...state.benefitFilters,
            [key]: { ...state.benefitFilters[key], ...patch },
          },
        })),

      resetBenefitFilters: () => set({ benefitFilters: emptyBenefitFilters() }),

      setSelectedFinalists: (ids) => set({ selectedFinalists: ids }),

      setRecommendation: (id) => set({ recommendation: id }),

      setGivebackPlanEnrolled: (enrolled) => set({ givebackPlanEnrolled: enrolled }),

      toggleComplianceItem: (id) =>
        set((state) => {
          const isChecked = state.complianceChecked.includes(id);
          if (isChecked) {
            // Uncheck: drop the timestamp too so the audit trail
            // reflects that the broker re-opened this item.
            const { [id]: _removed, ...rest } = state.complianceTimestamps;
            return {
              complianceChecked: state.complianceChecked.filter((x) => x !== id),
              complianceTimestamps: rest,
            };
          }
          return {
            complianceChecked: [...state.complianceChecked, id],
            complianceTimestamps: {
              ...state.complianceTimestamps,
              [id]: new Date().toISOString(),
            },
          };
        }),

      confirmDisclaimer: (id) =>
        set((state) => {
          if (state.disclaimersConfirmed.includes(id)) return state;
          return {
            disclaimersConfirmed: [...state.disclaimersConfirmed, id],
            disclaimerTimestamps: {
              ...state.disclaimerTimestamps,
              [id]: new Date().toISOString(),
            },
          };
        }),

      resetSession: () => set(initialState()),
    }),
    {
      // Safety-net persistence: notes survive a page refresh mid-session.
      // Key and partialize intentionally scope this to notes only — the rest
      // of the session (client intake, meds, providers, plans, compliance)
      // is intentionally ephemeral and re-entered per session.
      name: 'pm_session_notes',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ notes: state.notes }),
    },
  ),
);

export const selectNotes = (s: SessionStore): SessionNote[] => s.notes;
export const selectNoteCount = (s: SessionStore): number => s.notes.length;
